import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatNumber } from "@/lib/api";
import { browserLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import MessageCard from "@/components/MessageCard";
import AnimatedCounter from "@/components/AnimatedCounter";
import HomeScrollStory from "@/components/HomeScrollStory";

export default function HomePage() {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ voices: 0, reached: 0, messages: 0, countries: 0 });
  const { requireAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const lang = browserLang();
    api.get(`/messages?filter=loud&limit=6&lang=${encodeURIComponent(lang)}`)
      .then((r) => setMessages(r.data)).catch(() => {});
    api.get("/explore/stats")
      .then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-16 sm:pt-32 pb-20 sm:pb-32">
        <div className="oh-label oh-reveal">VOIHU · voice + humanity</div>
        <h1 className="font-serif-editorial text-[15vw] sm:text-[10vw] lg:text-[9rem] leading-[0.92] tracking-tight mt-6 oh-reveal oh-reveal-d1"
            data-testid="hero-title">
          One message<br />can move<br />the world.
        </h1>
        <p className="mt-10 sm:mt-14 max-w-xl text-lg sm:text-xl text-[color:var(--oh-ink-2)] leading-relaxed oh-reveal oh-reveal-d2">
          Let your voice travel. One human. One voice.
        </p>

        <div className="mt-12 flex flex-wrap gap-4 oh-reveal oh-reveal-d3">
          <Link to="/explore" className="btn-primary" data-testid="hero-explore">
            Explore messages
          </Link>
          <button onClick={() => requireAuth(() => navigate("/create"))}
                  className="btn-ghost" data-testid="hero-start">
            Start a message
          </button>
        </div>
      </section>

      <HomeScrollStory />

      <section className="border-t border-b oh-hairline">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10 sm:py-14 grid grid-cols-2 sm:grid-cols-4 gap-8">
          <Stat label="Voices" value={stats.voices} testid="stat-voices" />
          <Stat label="Reached" value={stats.reached} testid="stat-reached" />
          <Stat label="Messages" value={stats.messages} testid="stat-messages" />
          <Stat label="Countries" value={stats.countries} testid="stat-countries" />
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="oh-label">Loud right now</div>
            <h2 className="font-serif-editorial text-4xl sm:text-5xl mt-3">Messages the world is holding.</h2>
          </div>
          <Link to="/explore" className="oh-label hover:text-[color:var(--oh-red)] transition-colors hidden sm:inline"
                data-testid="home-explore-link">
            All messages →
          </Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="loud-grid">
          {messages.length === 0 ? (
            <div className="col-span-full oh-label text-[color:var(--oh-ink-2)]"
                 data-testid="loud-empty">
              No message here yet. Start one.
            </div>
          ) : (
            messages.map((m, i) => (<MessageCard key={m.id} m={m} i={i} />))
          )}
        </div>
        {messages.length > 0 && (
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
               data-testid="loud-cta-row">
            <p className="oh-label text-[color:var(--oh-ink-2)]">
              More messages are travelling right now →
            </p>
            <Link to="/explore" className="btn-red"
                  data-testid="home-explore-all">
              Explore all messages
            </Link>
          </div>
        )}
      </section>

      <footer className="border-t oh-hairline">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10 flex flex-wrap items-center justify-between gap-4 oh-label">
          <span>© VOIHU · Let your voice travel.</span>
          <div className="flex items-center gap-6">
            <a href="/terms" className="hover:text-[color:var(--oh-ink)] transition-colors"
               data-testid="footer-terms-link">
              Terms
            </a>
            <a href="/privacy" className="hover:text-[color:var(--oh-ink)] transition-colors"
               data-testid="footer-privacy-link">
              Privacy
            </a>
            <a href="/cookies" className="hover:text-[color:var(--oh-ink)] transition-colors"
               data-testid="footer-cookies-link">
              Cookies
            </a>
            <a href="/support" className="hover:text-[color:var(--oh-ink)] transition-colors"
               data-testid="footer-support-link">
              Support
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="oh-label">{label}</div>
      <div className="text-3xl sm:text-5xl font-light mt-2">
        <AnimatedCounter value={value} testid={testid} />
      </div>
    </div>
  );
}
